import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auth } from '../firebase/config';
import { createDiagnostic, canCreateDiagnostic } from '../services/diagnosticService';
import { buySingleDiagnostic } from '../services/stripeService';
import { DIAGNOSTIC_AXES } from '../services/diagnosticAnalysisService';

function NewDiagnostic() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = auth.currentUser;
  
  const [organization, setOrganization] = useState('');
  const [projectName, setProjectName] = useState('');
  const [projectManager, setProjectManager] = useState('');
  const [participants, setParticipants] = useState('');
  const [responses, setResponses] = useState(Array(24).fill(0));
  const [loading, setLoading] = useState(false);
  const [canCreate, setCanCreate] = useState(null);

  // Langue courante
  const lang = i18n.language || 'fr';

  // Échelle de réponse traduite
  const scaleLabels = lang === 'fr' 
    ? ['Pas du tout vrai', 'Peu vrai', 'Moyennement vrai', 'Plutôt vrai', 'Totalement vrai']
    : ['Not at all true', 'Slightly true', 'Moderately true', 'Mostly true', 'Completely true'];

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const checkDiagnosticLimit = async () => {
      const result = await canCreateDiagnostic(user.uid);
      setCanCreate(result);
    };

    checkDiagnosticLimit();
  }, [user, navigate]);

  const handleResponseChange = (questionIndex, value) => {
    const newResponses = [...responses];
    newResponses[questionIndex] = parseInt(value);
    setResponses(newResponses);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!canCreate?.canCreate) {
      alert(t('usedAll'));
      return;
    }

    if (responses.some(r => r === 0)) {
      alert(t('answerAllQuestions'));
      return;
    }

    setLoading(true);

    const diagnosticData = {
      projectName,
      organization,
      projectManager,
      participants: parseInt(participants),
      responses,
      type: 'soignez-votre-projet',
      completedAt: new Date()
    };

    const result = await createDiagnostic(user.uid, diagnosticData);

    if (result.success) {
      navigate(`/diagnostic-result/${result.diagnosticId}`);
    } else {
      alert(`${t('error')}: ${result.error}`);
    }

    setLoading(false);
  };

  if (!canCreate) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">{t('loading')}</div>
      </div>
    );
  }

  if (!canCreate.canCreate) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {t('limitReached')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('usedAll')}
          </p>
          
          <div className="bg-primary-50 border-2 border-primary-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              {t('quickSolution')}
            </h3>
            <p className="text-gray-700 mb-4">
              {t('buySingle')}
            </p>
            <div className="text-3xl font-bold text-primary-600 mb-4">{t('buyPrice')}</div>
            <button
              onClick={async () => {
                const result = await buySingleDiagnostic(user.uid, user.email);
                if (!result.success) {
                  alert(t('paymentError') + ': ' + result.error);
                }
              }}
              className="w-full btn-primary mb-3"
            >
              {t('buyButton')}
            </button>
          </div>

          <div className="space-y-3">
            {canCreate.subscriptionType === 'free' && (
              <button
                onClick={() => navigate('/pricing')}
                className="w-full btn-primary"
              >
                {t('seeSubscriptions')}
              </button>
            )}
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full btn-secondary"
            >
              {t('cancel')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  let questionIndex = 0;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-primary-600 hover:text-primary-700 font-semibold"
          >
            {t('backDashboard')}
          </button>
        </div>

        <div className="card">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('title')}
          </h1>
          <p className="text-gray-600 mb-6">
            {t('description')}
            {canCreate.remaining && (
              <span className="text-primary-600 font-semibold ml-2">
                ({canCreate.remaining} {t('remaining')})
              </span>
            )}
          </p>

          {/* Mode d'administration */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <h3 className="font-bold text-blue-900 mb-3">{t('administrationMode')}</h3>
            <ul className="text-sm text-blue-800 space-y-2">
              <li>• {t('duration')}</li>
              <li>• {t('participantsInfo')}</li>
              <li>• {t('method')}</li>
              <li>• {t('result')}</li>
            </ul>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Informations du projet */}
            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('projectInfo')}</h3>
              
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  {t('organization')}
                </label>
                <input
                  type="text"
                  className="input-field"
                  placeholder={t('organizationPlaceholder')}
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  {t('projectName')}
                </label>
                <input
                  type="text"
                  className="input-field"
                  placeholder={t('projectNamePlaceholder')}
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  {t('projectManager')}
                </label>
                <input
                  type="text"
                  className="input-field"
                  placeholder={t('projectManagerPlaceholder')}
                  value={projectManager}
                  onChange={(e) => setProjectManager(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  {t('participants')}
                </label>
                <input
                  type="number"
                  min="2"
                  max="10"
                  className="input-field"
                  placeholder={t('participantsPlaceholder')}
                  value={participants}
                  onChange={(e) => setParticipants(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Échelle de notation */}
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <h4 className="font-bold text-primary-900 mb-3">{t('scaleTitle')}</h4>
              <div className="grid grid-cols-5 gap-2 text-sm text-primary-800">
                {scaleLabels.map((label, i) => (
                  <div key={i} className="text-center">
                    <div className="font-bold">{i + 1}</div>
                    <div>{label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Questions par axe */}
            {DIAGNOSTIC_AXES.map((axis, axisIndex) => {
              const axisName = lang === 'fr' ? axis.name : (axis.nameEn || axis.name);
              const axisQuestions = lang === 'fr' ? axis.questions : (axis.questionsEn || axis.questions);
              
              return (
                <div key={axis.id} className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b border-gray-200">
                    {axisIndex + 1}. {axisName}
                  </h3>
                  
                  <div className="space-y-6">
                    {axisQuestions.map((question, qIndex) => {
                      const currentQuestionIndex = questionIndex++;
                      return (
                        <div key={currentQuestionIndex} className="bg-gray-50 p-4 rounded-lg">
                          <label className="block text-gray-800 font-medium mb-3">
                            {currentQuestionIndex + 1}. {question}
                          </label>
                          <div className="flex gap-4 justify-center">
                            {[1, 2, 3, 4, 5].map((value) => (
                              <label
                                key={value}
                                className={`flex flex-col items-center cursor-pointer p-3 rounded-lg transition-all ${
                                  responses[currentQuestionIndex] === value
                                    ? 'bg-primary-600 text-white scale-110'
                                    : 'bg-white hover:bg-gray-100'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name={`question-${currentQuestionIndex}`}
                                  value={value}
                                  checked={responses[currentQuestionIndex] === value}
                                  onChange={(e) => handleResponseChange(currentQuestionIndex, e.target.value)}
                                  className="sr-only"
                                  required
                                />
                                <span className="text-2xl font-bold">{value}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Boutons de soumission */}
            <div className="flex gap-4 pt-6">
              <button
                type="submit"
                className="flex-1 btn-primary py-4 text-lg"
                disabled={loading}
              >
                {loading ? t('submitGenerating') : t('submitButton')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="flex-1 btn-secondary py-4 text-lg"
              >
                {t('cancelButton')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default NewDiagnostic;
